import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, FormGroup, UntypedFormBuilder, Validators} from '@angular/forms';

import {ActivatedRoute, Router} from '@angular/router';
import { HttpParams } from '@angular/common/http';
import {TargetAudienceService} from 'src/app/shared/target-audience/target-audience.service';
import {ProductService} from 'src/app/shared/services/product.service';
import {NotificationsService} from 'src/app/admin/notifications/services/notifications.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {LayoutAlertService} from 'src/app/admin/layout-alert.service';

@Component({
    selector: 'app-notifications-shared-v2',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    standalone: false
})
export class NotificationsV2SharedComponent implements OnInit, OnDestroy {
  dataLoaded = false;
  form: FormGroup;
  wingameUuid: string;
  hiddenWingame: boolean;

  messageTypes = [
    {value: 1, text: 'pre-defined'},
    {value: 2, text: 'custom'},
  ];
  predefMessagesLoaded = false;
  predefMessages;
  expirationInfo = [
    {text: '', language: 'Dutch'},
    {text: '', language: 'English'},
  ];
  currentTabLang: string = 'Dutch';
  selectedTargetAudience = {included: {}, excluded: {}};
  predefinedAudience = {included: {}, excluded: {}};
  targetAudience;
  targetAudienceId;

  soundOptions = [
    {value: 0, text: 'Default value'},
    {value: 1, text: 'No sound'},
  ];

  soundOptionsNames = ['Default value', 'No sound'];
  deliveryStatuses = [];
  eventId;

  deeplinks = {
    wingame: 'beappic://v2/win/', // requires game uuid
    event: 'beappic://event/', // requires event id
    voucher: 'beappic://shop/', //
  };
  statusAllowPublish = false;
  showTA = false;
  statusId;
  initialSoundOption = 1;
  blockSendButton = false;

  private destroy$ = new Subject<void>();

  @Input() actor_id;
  @Input() actor_type;
  @Input() verbFilters;
  @Input() template_mode = false;

  @Output() formValidity = new EventEmitter();
  @Output() deliveryStatusCreated = new EventEmitter<number>();

  constructor(
    private fb: UntypedFormBuilder,
    private notificationsService: NotificationsService,
    private route: ActivatedRoute,
    private router: Router,
    private targetAudienceService: TargetAudienceService,
    private layoutAlertService: LayoutAlertService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.statusId = params['id'];
      }
    });
    if (this.actor_id) {
      this.form = this.createForm();
      this.setValidators();
      this.getNotificationDeliveryStatuses();
    }
  }

  setDeeplink() {
    if (this.verbFilters === 'win_games') {
      const event_id = this.eventId;
      let deeplink;
      if (this.hiddenWingame || !event_id) {
        deeplink = `${this.deeplinks.wingame}${this.wingameUuid}`;
      } else {
        deeplink = `${this.deeplinks.event}${event_id}`;
      }
      this.form.patchValue({deeplink});
    } else if (this.verbFilters === 'products') {
      this.form.patchValue({
        deeplink: `${this.deeplinks.event}${this.eventId}`,
      });
    }
  }

  getNotificationDeliveryStatuses() {
    let actorObjk;
    let params;
    if (this.verbFilters === 'win_games') {
      actorObjk = {object_id: this.actor_id, type: 'wingame'};
      const actorString = JSON.stringify(actorObjk);
      params = new HttpParams().set('actor', actorString).set('page', '1');
    } else if (this.verbFilters === 'products') {
      actorObjk = {object_id: this.eventId, type: 'event'};
      const actorString = JSON.stringify(actorObjk);
      params = new HttpParams().set('actor', actorString).set('page', '1').set('category', 'products');
    } else if (this.verbFilters === 'voucher') {
      actorObjk = {object_id: this.actor_id, type: 'voucher'};
      const actorString = JSON.stringify(actorObjk);
      params = new HttpParams().set('actor', actorString).set('page', '1');
    }

    if (actorObjk) {
      this.notificationsService.deliveryStatuses(params).subscribe((data: any) => {
        this.deliveryStatuses = data.results;
        this.dataLoaded = true;
      });
    }
  }

  getPredefinedMessages() {
    this.notificationsService.getNotificationsVerbs({verb_filters: this.verbFilters}).subscribe((data: any) => {
      this.predefMessages = data.results;
    });
  }

  createForm() {
    const currentDate = new Date();
    const formGroup = this.fb.group({
      actor: {type: this.verbFilters === 'voucher' ? 'voucher' : 'wingame', id: this.actor_id},
      message_type: ['', Validators.required],
      pre_defined_message: [null],
      event: [null],
      deeplink: ['', Validators.required],
      sound: [1, Validators.required],
      scheduled_at: [currentDate.toISOString().slice(0, 16)],
      if_scheduled: [false],
      message: this.fb.array([]),
      target_audience: this.template_mode
        ? [null]
        : [this.targetAudienceService.stringifiedAudience(this.selectedTargetAudience), Validators.required],
      existing_target_audience_id: [null],
      display_on_event: [null],
      verb_group: 3,
    });

    if (this.eventId) {
      formGroup.get('event').setValue(this.eventId);
    }

    formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formValidity.emit(formGroup.valid);
    });

    const expirationInfoArray = formGroup.get('message') as FormArray;

    this.expirationInfo.forEach((info, index) => {
      const lang = info.language.toLowerCase();
      const expirationGroup = this.fb.group({
        text: [info.text],
        display: [false],
        language: [info.language],
      });
      expirationInfoArray.push(expirationGroup);
    });

    expirationInfoArray.controls.forEach((control: FormGroup) => {
      control
        .get('display')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          const firstAvailableLang = this.findAvailableLang();
          if (firstAvailableLang) {
            this.currentTabLang = firstAvailableLang.value.language;
          }
        });
    });

    formGroup
      .get('message_type')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data === 'pre-defined' && !this.predefMessagesLoaded) {
          this.predefMessagesLoaded = true;
          this.getPredefinedMessages();
        }
        const preDefinedMessage = formGroup.get('pre_defined_message');
        preDefinedMessage.clearValidators();
        if (data === 'pre-defined') {
          preDefinedMessage.setValidators(Validators.required);
        }
        preDefinedMessage.updateValueAndValidity();
      });

    formGroup
      .get('if_scheduled')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (!data) {
          formGroup.get('scheduled_at').setValue(currentDate.toISOString().slice(0, 16));
        }
      });

    return formGroup;
  }

  setValidators() {
    const ifScheduled = this.form.get('if_scheduled');
    const scheduledAt = this.form.get('scheduled_at');
    ifScheduled.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value) {
        scheduledAt.setValidators(Validators.required);
        scheduledAt.updateValueAndValidity();
      } else {
        scheduledAt.clearValidators();
        scheduledAt.updateValueAndValidity();
      }
    });
  }

  onTextSelected(selectedText: string) {
    this.form.get('pre_defined_message').setValue(selectedText);
  }

  findAvailableLang(): FormGroup | null {
    const firstFormGroupWithDisplayTrue = this.expirationInfoFA.controls.find((control: FormGroup) => {
      return control.get('display').value === true;
    });
    return firstFormGroupWithDisplayTrue ? (firstFormGroupWithDisplayTrue as FormGroup) : null;
  }

  anyDisplayTrue(): boolean {
    const expirationInfoArray = this.form.get('message') as FormArray;
    for (let i = 0; i < expirationInfoArray.length; i++) {
      const expirationGroup = expirationInfoArray.at(i) as FormGroup;
      if (expirationGroup.get('display').value) {
        return true;
      }
    }
    return false;
  }

  showTab(tabName: string) {
    this.currentTabLang = tabName;
  }

  onAudienceSaved(audience, forAlreadyAddedNotif?: boolean) {
    if (forAlreadyAddedNotif) {
      this.notificationsService.updateAudience(this.targetAudienceId, audience).subscribe(
        data => {
          this.layoutAlertService.success("Notification's Target Audience saved.", 2);
        },
        error => {
          this.layoutAlertService.error(
            'Error during saving Target Audience. If this situation persists, contact your administrator.',
          );
          console.error('Error while saving Target Audience:', error);
        },
      );
    } else {
      if (Object.keys(audience.included).length > 0 || Object.keys(audience.excluded).length > 0) {
        this.targetAudience = audience;
        this.form.get('target_audience').setValue(audience);
      } else {
        this.targetAudience = null;
      }
    }
  }

  prepareRequest(request) {
    const req = {...request};

    if (this.verbFilters === 'products') {
      ((req.verb_label = 3), delete req.actor);
    }

    if (req.message_type === 'pre-defined') {
      req.message = {};
      req.message_type = 'pre_defined';
    } else if (req.message_type === 'custom') {
      const mess = [...req.message];
      let messOb = {nl: '', en: ''};
      mess.forEach(item => {
        if (item.language === 'Dutch' && item.display) {
          messOb.nl = item.text;
        } else if (item.language === 'English' && item.display) {
          messOb.en = item.text;
        }
      });
      req.message = messOb;
      delete req.pre_defined_message;
    }

    if (!req.if_scheduled) {
      req.scheduled_at = null;
    }

    if (!this.eventId) {
      delete req.event;
    }

    delete req.existing_target_audience_id;
    delete req.display_on_event;
    delete req.if_scheduled;
    return req;
  }

  send() {
    this.blockSendButton = true;
    const req = this.prepareRequest(this.form.value);
    if (this.actor_id) {
      this.notificationsService.postNotifications(req).subscribe(
        (data: any) => {
          this.layoutAlertService.success('Notification saved.', 2);
          this.clearForm();
          this.blockSendButton = false;
          this.deliveryStatusCreated.emit(data.delivery_status_id);
          this.getNotificationDeliveryStatuses();
        },
        error => {
          this.blockSendButton = false;
          this.layoutAlertService.error(
            `Error during saving Wingame notifications. If this situation persists, contact your administrator. \n Error: ${error}`,
          );
          console.error('Error while saving wingame:', error);
        },
      );
    }
  }

  showTargetAudience(id, updateMode?: boolean) {
    this.targetAudienceId = id;
    this.notificationsService.targetAudiences(id).subscribe(
      (data: any) => {
        if (updateMode) {
          this.selectedTargetAudience = data.audience_conf;
        } else {
          this.predefinedAudience = data.audience_conf;
        }
      },
      error => {
        this.layoutAlertService.error(
          'Error during getting Wingame target audience. If this situation persists, contact your administrator.',
        );
        console.error('Error while getting Wingame target audience:', error);
      },
    );
  }

  cancelNotification(notifId) {
    this.notificationsService.cancelNotification(notifId).subscribe(
      data => {
        this.layoutAlertService.success('Notification canceled.', 2);
        this.getNotificationDeliveryStatuses();
      },
      error => {
        this.layoutAlertService.error(
          'Error during canceling notification. If this situation persists, contact your administrator.',
        );
        console.error('Error while canceling notification:', error);
      },
    );
  }

  soundSelected(event) {
    const item = this.soundOptions.find(option => option.text === event);
    this.form.get('sound').setValue(item.value);
  }

  clearForm() {
    this.form.reset();
    this.form = this.createForm();
    this.setDeeplink();
  }

  get expirationInfoFA() {
    return this.form.get('message') as FormArray;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
